import { formatOrderTotal, Order, OrderItem } from "../orders/order.types";
import { OrderEvalContentArgs } from "./content.eval.types";

export class OrderContentProvider {
  private order: Order;
  constructor(order: Order) {
    this.order = order;
  }

  resolveOrderValue(field: string): string {
    const fieldName = field.toLowerCase().replace(/\s+/g, "");

    switch (fieldName) {
      case "orderid":
        return this.order.id;
      case "ordernumber":
      case "number":
        return this.order.number;
      case "firstname":
        return this.order.firstName;
      case "lastname":
        return this.order.lastName;
      case "organization":
        return this.order.organization;
      case "email":
        return this.order.email;
      case "phone":
        return this.order.phone;
      case "address":
        return this.order.address;
      case "comments":
        return this.order.comments;
      case "total":
        return this.formatCurrency(this.order.total);
      case "subtotal":
        return this.formatCurrency(this.order.subTotal);
      case "salestax":
        return this.formatCurrency(this.order.salesTax);
      case "deliveryfee":
        return this.formatCurrency(this.order.deliveryFee);
      case "discount":
        return this.formatCurrency(this.order.discount);
      case "currency":
        return this.order.currency;
      default:
        // Check custom fields
        const customValue = this.order.advanced?.fields?.[field];
        if (customValue !== undefined) {
          return customValue;
        }
        return "";
    }
  }

  resolveItemValue(item: OrderItem, field: string): string {
    const fieldArgs = field.toLowerCase().replace(/\s+/g, "").split(":");
    const fieldName = fieldArgs[0];

    switch (fieldName) {
      case "code":
        return item.code;
      case "name":
        return item.name;
      case "variant":
        return item.variant || "";
      case "price":
        return this.formatCurrency(item.price);
      case "quantity":
        return item.quantity.toString();
      case "itemtotal":
        return this.formatCurrency(
          item.quantity * item.price - (item.discount || 0)
        );
      default:
        const customValue = Object.entries(item.fields || {}).find(
          ([key]) => key.toLowerCase() === fieldName
        );
        if (customValue !== undefined) {
          return customValue[1];
        }
        return "";
    }
  }

  private formatCurrency(amount: number): string {
    return amount !== undefined && !isNaN(amount)
      ? formatOrderTotal(amount, this.order.currency)
      : "";
  }

  processItemsBlock(blockContent: string, _args: OrderEvalContentArgs): string {
    if (!this.order.items || this.order.items.length === 0) {
      return "";
    }

    let result = "";
    const fieldRe = /\{\{([^}]+)\}\}/gi;

    for (let i = 0; i < this.order.items.length; i++) {
      const item = this.order.items[i];
      let itemContent = blockContent;

      itemContent = itemContent.replace(fieldRe, (_match, fieldName) => {
        return this.resolveItemValue(item, fieldName.trim());
      });

      result += itemContent;
    }

    return result;
  }
}

// Utility function to generate sample order data for preview
// export function generateSampleOrderData(
//   customFields: { [key: string]: string } = {}
// ): OrderData {
//   return {
//     id: "sample-order-1",
//     orderId: "12345",
//     number: "ORD-12345",
//     firstName: "John",
//     lastName: "Doe",
//     organization: "Acme Corporation",
//     email: "john.doe@example.com",
//     phone: "(555) 123-4567",
//     address: "123 Main St, Anytown, ST 12345",
//     total: 157.5,
//     subTotal: 135.0,
//     salesTax: 10.8,
//     deliveryFee: 15.0,
//     discount: 3.3,
//     currency: "USD",
//     items: [
//       {
//         id: "item-1",
//         itemId: "prod-1",
//         variantId: 1,
//         code: "WIDGET-001",
//         name: "Premium Widget",
//         variant: "Blue, Large",
//         price: 75.0,
//         quantity: 1,
//         discount: 0,
//         category: "Widgets",
//         tag: "Premium",
//         image: "https://via.placeholder.com/100x100/4CAF50/white?text=Widget",
//         fields: {
//           Description: "High-quality premium widget",
//           SKU: "WDG-PRM-BL-L",
//         },
//       },
//       {
//         id: "item-2",
//         itemId: "prod-2",
//         code: "GADGET-002",
//         name: "Smart Gadget",
//         price: 60.0,
//         quantity: 1,
//         discount: 3.3,
//         category: "Gadgets",
//         tag: "Smart",
//         image: "https://via.placeholder.com/100x100/2196F3/white?text=Gadget",
//         fields: {
//           Description: "Innovative smart gadget",
//           SKU: "GDG-SMT-001",
//         },
//       },
//     ],
//     customFields: {
//       "Special Instructions": "Please handle with care",
//       "Delivery Date": "2025-07-30",
//       ...customFields,
//     },
//   };
// }
